(function (root, document) {
  'use strict';

  function debug(message, details) {
    var config = root.DisplayZoneDeliveryConfig;

    if (config && config.debug && root.console) {
      root.console.warn('[Display Zone delivery estimate] ' + message, details || '');
    }
  }

  function cloneProfile(profile) {
    return JSON.parse(JSON.stringify(profile));
  }

  function findProfile(config, core, profileKey, vendorName) {
    var requestedKey = core.normalizeKey(profileKey);
    var normalizedVendor = core.normalizeKey(vendorName);
    var entries = Object.keys(config.profiles || {});
    var directKey;

    if (requestedKey) {
      directKey = entries.find(function (key) {
        return core.normalizeKey(key) === requestedKey;
      });

      if (directKey) {
        return { key: directKey, profile: cloneProfile(config.profiles[directKey]) };
      }
    }

    for (var index = 0; index < entries.length; index += 1) {
      var key = entries[index];
      var profile = config.profiles[key];
      var vendorNames = Array.isArray(profile.vendorNames) ? profile.vendorNames : [];
      var matchesKey = core.normalizeKey(key) === normalizedVendor;
      var matchesAlias = vendorNames.some(function (name) {
        return core.normalizeKey(name) === normalizedVendor;
      });

      if (matchesKey || matchesAlias) {
        return { key: key, profile: cloneProfile(profile) };
      }
    }

    return null;
  }

  function applyIntegerOverride(profile, dataset, datasetKey, profileKey) {
    if (dataset[datasetKey] !== undefined && dataset[datasetKey] !== '') {
      profile[profileKey] = dataset[datasetKey];
    }
  }

  function applyProductOverrides(profile, dataset) {
    applyIntegerOverride(profile, dataset, 'preparationMin', 'preparationMinBusinessDays');
    applyIntegerOverride(profile, dataset, 'preparationMax', 'preparationMaxBusinessDays');
    applyIntegerOverride(profile, dataset, 'transitMin', 'transitMinBusinessDays');
    applyIntegerOverride(profile, dataset, 'transitMax', 'transitMaxBusinessDays');
    applyIntegerOverride(profile, dataset, 'extraBuffer', 'extraBufferBusinessDays');

    if (dataset.estimateNote) {
      profile.note = dataset.estimateNote;
    }

    return profile;
  }

  function countryIsSupported(config, countryCode) {
    var supported = config.supportedCountryCodes;

    if (!Array.isArray(supported) || !supported.length || !countryCode) {
      return true;
    }

    return supported.indexOf(String(countryCode).toUpperCase()) !== -1;
  }

  function dispatchEstimateEvent(element, resolved, result, formattedRange) {
    document.dispatchEvent(new CustomEvent('displayzone:delivery-estimate', {
      detail: {
        productHandle: element.dataset.productHandle || '',
        vendor: element.dataset.vendor || '',
        profileKey: resolved.key,
        earliest: result.earliestKey,
        latest: result.latestKey,
        formattedRange: formattedRange
      }
    }));
  }

  function setResponsiveDateRange(element, primary, range, fullRange, shortRange, resolved, result) {
    if (!primary || !range) {
      return;
    }

    range.textContent = fullRange;
    element.dataset.dateRangeFormat = 'long';

    if (primary.clientWidth > 0 && primary.scrollWidth > primary.clientWidth + 1) {
      range.textContent = shortRange;
      element.dataset.dateRangeFormat = 'short';
    }

    if (element.dataset.estimatedDeliveryText !== range.textContent) {
      element.dataset.estimatedDeliveryText = range.textContent;
      dispatchEstimateEvent(element, resolved, result, range.textContent);
    }
  }

  function renderEstimate(element, config, core, resolved) {
    var profile = applyProductOverrides(resolved.profile, element.dataset);
    var locale = element.dataset.locale || config.defaultLocale || 'en-US';
    var result = core.calculateEstimate(profile, new Date(), {
      defaultTimeZone: config.defaultTimeZone,
      defaultCutoffTimeLocal: config.defaultCutoffTimeLocal,
      defaultShippingWeekdays: config.defaultShippingWeekdays,
      globalExcludedDates: config.globalExcludedDates
    });

    if (!result.valid) {
      debug('Profile "' + resolved.key + '" is incomplete or invalid.', result.errors);
      return;
    }

    var fullRange = core.formatDateRange(
      result.earliest,
      result.latest,
      locale,
      config.includeYearInDisplayedRange === true,
      'long'
    );
    var shortRange = core.formatDateRange(
      result.earliest,
      result.latest,
      locale,
      config.includeYearInDisplayedRange === true,
      'short'
    );
    var label = element.querySelector('[data-dz-delivery-label]');
    var primary = element.querySelector('.dz-delivery-estimate__primary-text');
    var range = element.querySelector('[data-dz-delivery-range]');
    var note = element.querySelector('[data-dz-delivery-note]');

    if (label) {
      label.textContent = (config.text && config.text.label) || 'Estimated delivery';
    }

    if (note) {
      var noteText = profile.note || (config.text && config.text.defaultNote) || '';
      note.textContent = noteText;
      note.hidden = !noteText;
    }

    element.dataset.deliveryProfileKey = resolved.key;
    element.dataset.estimatedDeliveryStart = result.earliestKey;
    element.dataset.estimatedDeliveryEnd = result.latestKey;
    element.hidden = false;
    element.classList.add('dz-delivery-estimate--ready');

    var updateRange = function () {
      setResponsiveDateRange(element, primary, range, fullRange, shortRange, resolved, result);
    };

    updateRange();

    if (root.ResizeObserver && primary) {
      var resizeObserver = new root.ResizeObserver(updateRange);
      resizeObserver.observe(primary);
      element.dzDeliveryResizeObserver = resizeObserver;
    } else {
      root.addEventListener('resize', updateRange);
    }

    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(updateRange);
    }
  }

  function initializeElement(element) {
    if (element.dataset.dzDeliveryInitialized === 'true') {
      return;
    }

    element.dataset.dzDeliveryInitialized = 'true';

    var config = root.DisplayZoneDeliveryConfig;
    var core = root.DisplayZoneDeliveryCore;

    if (!config || !core) {
      debug('Required configuration or calculation file did not load.');
      return;
    }

    if (!countryIsSupported(config, element.dataset.countryCode)) {
      debug('The current country is not configured for delivery estimates.', element.dataset.countryCode);
      return;
    }

    var resolved = findProfile(
      config,
      core,
      element.dataset.profileKey,
      element.dataset.vendor
    );

    if (!resolved) {
      debug('No delivery profile matches vendor "' + (element.dataset.vendor || '') + '".');
      return;
    }

    if (resolved.profile.enabled !== true) {
      debug('Profile "' + resolved.key + '" is disabled.');
      return;
    }

    renderEstimate(element, config, core, resolved);
  }

  function initialize(scope) {
    var context = scope || document;
    var elements = [];

    if (context.matches && context.matches('[data-dz-delivery-estimate]')) {
      elements.push(context);
    }

    if (context.querySelectorAll) {
      elements = elements.concat(Array.prototype.slice.call(
        context.querySelectorAll('[data-dz-delivery-estimate]')
      ));
    }

    elements.forEach(initializeElement);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initialize(document);
    });
  } else {
    initialize(document);
  }

  document.addEventListener('shopify:section:load', function (event) {
    initialize(event.target);
  });
})(window, document);
